import React, { useEffect, useState } from 'react';
import { Alert, Button, Table, Modal, Select, Input, Tabs, Steps, Typography, message, Spin, Tag } from 'antd';
import { Link } from 'react-router-dom';
import { withAppContext } from 'context/AppContext';
import { buildAccessTokenDetailUri } from 'utils/routes';
import {
  useGetPaginatedExtAuthentications,
  getExtAccessHosts,
  getExtAuthenticationOptions,
  createAccessToken,
  getExtAuthenticationDetail,
  getPullNewPropertyCandidates,
  postCreateTokenPropertyCandidates
} from 'utils/apis/accessToken';
import { constructColumn, constructColumnFilterSearch, handleOnAggregationTableChange } from 'utils/table/table';
import { ACCESS_TOKEN_ADMINS, API_SCOPE_OPTIONS, apiScopeLabel } from 'utils/constants';
import { StyledCard, CardTitle, CardSubtitle } from './AccessToken.styles';
import { PropertyAllowlistEditorBody, PropertyAllowlistEditorActions } from './PropertyAllowlistEditor';
import {
  normalizeExtAuthDetailForAllowlist,
  normalizePullCandidateRows,
  mergeSyncedDetailRowsWithPullCandidates,
  enrichPullRowsWithHostFromDetail,
  buildFallbackPropertyRowsFromDetail,
  buildExtAccessesStubsFromWizardHosts,
  getManageSyncPrimaryButtonState,
  buildPropertyAllowlistItems,
  MANAGE_SYNC_MSG
} from './pullPropertyAllowlistShared';

const { Option } = Select;
const { Text } = Typography;

const isAccessTokenAdmin = user => user && user._id && ACCESS_TOKEN_ADMINS.includes(String(user._id));

/** `getExtAccessHosts` may return an array or `{ data: [...] }`. */
function normalizeExtAccessHostListResponse(hosts) {
  if (Array.isArray(hosts)) return hosts;
  if (hosts && Array.isArray(hosts.data)) return hosts.data;
  return [];
}

const AccessToken = props => {
  const { user } = props;
  const [query, setQuery] = useState({ currentPage: 1, limit: 10 });
  const { isLoading, paginatedData: tokenList, total, refetch } = useGetPaginatedExtAuthentications(query);
  const [createWizardOpen, setCreateWizardOpen] = useState(false);
  const [createWizardStep, setCreateWizardStep] = useState(0);
  const [createWizardMappingTab, setCreateWizardMappingTab] = useState('new');
  const [createWizardTokenName, setCreateWizardTokenName] = useState('');
  const [createWizardApiScopes, setCreateWizardApiScopes] = useState([]);
  const [createWizardParentHostId, setCreateWizardParentHostId] = useState(null);
  const [createWizardChildHostIds, setCreateWizardChildHostIds] = useState([]);
  const [createWizardTemplateId, setCreateWizardTemplateId] = useState(null);
  const [createWizardParentOptions, setCreateWizardParentOptions] = useState([]);
  const [createWizardChildHostsByParentId, setCreateWizardChildHostsByParentId] = useState({});
  const [createWizardTemplateOptions, setCreateWizardTemplateOptions] = useState([]);
  /** Full host objects from `getExtAccessHosts` (for externalId + child rows) */
  const [createWizardHostsTree, setCreateWizardHostsTree] = useState([]);
  const [createWizardPropertyRows, setCreateWizardPropertyRows] = useState([]);
  const [createWizardSelectedIds, setCreateWizardSelectedIds] = useState([]);
  const [createWizardHostGroupOrder, setCreateWizardHostGroupOrder] = useState([]);
  const [createWizardStep2Loading, setCreateWizardStep2Loading] = useState(false);
  const [createWizardCandidatesPartial, setCreateWizardCandidatesPartial] = useState(false);
  const [createWizardSubmitting, setCreateWizardSubmitting] = useState(false);

  const createWizardChildHostOptions =
    createWizardMappingTab === 'new' && createWizardParentHostId ? createWizardChildHostsByParentId[createWizardParentHostId] || [] : [];
  const createWizardAllowlistPrimary = getManageSyncPrimaryButtonState(createWizardPropertyRows, createWizardSelectedIds);

  const mapExternalId = row => row.externalId || row.externalID || row.external_id || row.extPropertyId || row.extId || '';
  const maskTokenForDropdown = token => {
    const value = token != null ? String(token) : '';
    if (!value) return '—';
    const visible = value.slice(0, 4);
    const hiddenLen = Math.max(0, value.length - visible.length);
    return visible + '*'.repeat(hiddenLen);
  };

  const parseCreateTokenResponse = res => {
    if (!res || typeof res !== 'object') return { id: null, token: null };
    const data = res._id != null || res.id != null || res.token != null ? res : res.data;
    if (!data || typeof data !== 'object') return { id: null, token: null };
    const id = data._id != null ? data._id : data.id;
    const token = data.token;
    return { id: id != null ? String(id) : null, token: token != null ? String(token) : null };
  };

  const buildPropertyAllowlistPayload = (rows, selectedIds) => {
    const idSet = new Set((selectedIds || []).map(String));
    const list = (rows || []).filter(r => idSet.has(String(r.id)));
    const items = buildPropertyAllowlistItems(list);
    const total = (rows || []).length;
    const scope = total > 0 && items.length === total ? 'all' : 'selected';
    return { scope, items, allowlistInputRowCount: list.length };
  };

  const submitCreateTokenWizard = async () => {
    if (!createWizardPropertyRows.length) {
      return message.warning('No properties loaded. Wait for the list to finish loading or adjust your mapping.');
    }
    const allowlistPrimary = getManageSyncPrimaryButtonState(createWizardPropertyRows, createWizardSelectedIds);
    if (allowlistPrimary.disabled) {
      if (allowlistPrimary.hostMissingSelection) {
        return message.warning(MANAGE_SYNC_MSG.eachHostNeedsOne);
      }
      return message.warning(MANAGE_SYNC_MSG.selectAtLeastOne);
    }
    const name = (createWizardTokenName && createWizardTokenName.trim()) || '';
    if (!name) {
      return message.warning('Please enter a token name');
    }
    if (createWizardMappingTab === 'new' && !createWizardParentHostId) {
      return message.warning('Please select a host');
    }
    if (createWizardMappingTab === 'reuse' && !createWizardTemplateId) {
      return message.warning('Please select an existing token');
    }

    const { scope, items, allowlistInputRowCount } = buildPropertyAllowlistPayload(createWizardPropertyRows, createWizardSelectedIds);
    if (items.length === 0) {
      return message.warning(MANAGE_SYNC_MSG.selectAtLeastOne);
    }
    if (items.length < allowlistInputRowCount) {
      return message.warning(MANAGE_SYNC_MSG.allowlistRowNotResolvable);
    }

    if (!createWizardApiScopes.length) {
      return message.warning('Please select at least one API scope');
    }

    const payload = {
      name,
      apiScopes: createWizardApiScopes,
      mapping:
        createWizardMappingTab === 'new'
          ? { type: 'new', hostIds: [createWizardParentHostId, ...createWizardChildHostIds] }
          : { type: 'reuse', extAuthenticationId: String(createWizardTemplateId) },
      propertyAllowlist: { scope, items }
    };

    setCreateWizardSubmitting(true);
    try {
      const created = await createAccessToken(payload);
      const { id } = parseCreateTokenResponse(created);
      if (!id) {
        return message.error('Failed to create token');
      }
      setCreateWizardOpen(false);
      setCreateWizardStep(0);
      refetch();
      message.success('Token created successfully');
    } catch (err) {
      message.error('Failed to create token');
    } finally {
      setCreateWizardSubmitting(false);
    }
  };

  const loadCreateWizardHostTree = () => {
    return getExtAccessHosts([], false, false)
      .then(hosts => {
        const list = normalizeExtAccessHostListResponse(hosts);
        const parent = list.map(h => ({
          id: String(h._id),
          name: h.name || ''
        }));
        const childMap = {};
        list.forEach(h => {
          const pid = String(h._id);
          childMap[pid] = (h.children || []).map(c => ({
            id: String(c._id),
            name: c.name || ''
          }));
        });
        setCreateWizardParentOptions(parent);
        setCreateWizardChildHostsByParentId(childMap);
        setCreateWizardHostsTree(list);
      })
      .catch(() => {
        setCreateWizardParentOptions([]);
        setCreateWizardChildHostsByParentId({});
        setCreateWizardHostsTree([]);
      });
  };

  const loadCreateWizardTemplateOptions = () => {
    return getExtAuthenticationOptions()
      .then(response => {
        const list = Array.isArray(response) ? response : response && response.data ? response.data : [];
        const tokenOptions = list.map(t => ({
          id: String(t._id || t.id || t.extAuthenticationId),
          name: t.name || t.token || 'Token',
          token: t.token || ''
        }));
        setCreateWizardTemplateOptions(tokenOptions);
      })
      .catch(() => {
        setCreateWizardTemplateOptions([]);
      });
  };

  useEffect(() => {
    if (!createWizardOpen || createWizardStep !== 1) return undefined;

    let cancelled = false;
    setCreateWizardStep2Loading(true);
    setCreateWizardCandidatesPartial(false);

    const finish = () => {
      if (!cancelled) setCreateWizardStep2Loading(false);
    };

    const run = async () => {
      try {
        if (createWizardMappingTab === 'reuse' && createWizardTemplateId) {
          const detailRaw = await getExtAuthenticationDetail(createWizardTemplateId);
          const detail = normalizeExtAuthDetailForAllowlist(detailRaw);
          if (cancelled || !detail) {
            finish();
            return;
          }
          setCreateWizardHostGroupOrder(detail.extAccesses || []);
          let apiRows = [];
          let usedFallback = false;
          try {
            const data = await getPullNewPropertyCandidates(String(createWizardTemplateId));
            apiRows = normalizePullCandidateRows(data);
          } catch (e) {
            usedFallback = true;
          }
          const syncedRows = enrichPullRowsWithHostFromDetail(buildFallbackPropertyRowsFromDetail(detail), detail);
          const merged = mergeSyncedDetailRowsWithPullCandidates(syncedRows, apiRows);
          const rows = enrichPullRowsWithHostFromDetail(merged, detail);
          if (cancelled) return;
          setCreateWizardPropertyRows(rows);
          setCreateWizardSelectedIds(rows.filter(r => r.alreadyOnToken).map(r => r.id));
          setCreateWizardCandidatesPartial(usedFallback);
          if (usedFallback) {
            message.warning('Full PMS list was not available for this template. Showing properties already on the template token.');
          }
        } else if (createWizardMappingTab === 'new' && createWizardParentHostId) {
          const hostIds = [createWizardParentHostId, ...createWizardChildHostIds];
          const stubs = buildExtAccessesStubsFromWizardHosts(
            createWizardHostsTree,
            createWizardParentHostId,
            createWizardChildHostIds,
            mapExternalId
          );
          setCreateWizardHostGroupOrder(stubs);
          let apiRows = [];
          let usedFallback = false;
          try {
            const data = await postCreateTokenPropertyCandidates(hostIds);
            apiRows = normalizePullCandidateRows(data);
          } catch (e) {
            usedFallback = true;
          }
          if (!apiRows.length) usedFallback = true;
          const syntheticDetail = { extAccesses: stubs };
          // For brand-new token creation, we must not drop `alreadyOnToken` rows.
          // `mergeSyncedDetailRowsWithPullCandidates([], apiRows)` would treat `alreadyOnToken`
          // as "belongs to an existing token" and filter it out because there is no syncedRows.
          const rows = enrichPullRowsWithHostFromDetail(apiRows, syntheticDetail);
          if (cancelled) return;
          setCreateWizardPropertyRows(rows);
          setCreateWizardSelectedIds(rows.map(r => r.id));
          setCreateWizardCandidatesPartial(usedFallback);
          if (usedFallback) {
            message.warning(
              'Could not load PMS properties for the selected hosts. Implement POST /extAuthentication/create-token/property-candidates (same response shape as pull-new candidates).'
            );
          }
        } else {
          setCreateWizardPropertyRows([]);
          setCreateWizardSelectedIds([]);
          setCreateWizardHostGroupOrder([]);
        }
      } finally {
        finish();
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    createWizardOpen,
    createWizardStep,
    createWizardMappingTab,
    createWizardParentHostId,
    createWizardChildHostIds,
    createWizardTemplateId,
    createWizardHostsTree
  ]);

  const openCreateTokenWizard = () => {
    setCreateWizardOpen(true);
    setCreateWizardStep(0);
    setCreateWizardMappingTab('new');
    setCreateWizardTokenName('');
    setCreateWizardApiScopes([]);
    setCreateWizardParentHostId(null);
    setCreateWizardChildHostIds([]);
    setCreateWizardTemplateId(null);
    setCreateWizardHostsTree([]);
    setCreateWizardPropertyRows([]);
    setCreateWizardSelectedIds([]);
    setCreateWizardHostGroupOrder([]);
    setCreateWizardCandidatesPartial(false);
    setCreateWizardStep2Loading(false);
    setCreateWizardSubmitting(false);
    loadCreateWizardHostTree();
  };

  const columns = [
    {
      ...constructColumn('Name', 'name'),
      ...constructColumnFilterSearch('name', 'Search Name'),
      width: 280,
      render: (value, record) => (
        <div>
          <span style={{ fontWeight: 500 }}>{value || '—'}</span>
          <div style={{ fontSize: 11, color: '#aaa' }}>{record.hostName}</div>
        </div>
      )
    },
    {
      ...constructColumn('Token', 'token'),
      ...constructColumnFilterSearch('token', 'Search Token'),
      width: 160,
      render: (value, record) =>
        value ? (
          <Link to={buildAccessTokenDetailUri(String(record._id))} style={{ fontFamily: 'monospace', fontSize: 12 }}>
            {value.slice(0, 4) + '*'.repeat(Math.max(0, value.length - 4))}
          </Link>
        ) : (
          '—'
        )
    },
    {
      ...constructColumn('API scope', 'apiScopes'),
      width: 220,
      render: value => {
        const scopes = Array.isArray(value) ? value : [];
        if (!scopes.length) return '—';
        return scopes.map(scope => (
          <Tag key={scope} color={scope === 'messaging' ? 'purple' : 'blue'}>
            {apiScopeLabel(scope)}
          </Tag>
        ));
      }
    }
  ];

  if (!isAccessTokenAdmin(user)) {
    return (
      <StyledCard title={<CardTitle>Access Tokens</CardTitle>} style={{ textAlign: 'center', padding: '48px 24px' }}>
        <p style={{ marginBottom: 16, color: '#666' }}>You don&apos;t have access to this page.</p>
        <Link to="/overview">Back to Overview</Link>
      </StyledCard>
    );
  }

  return (
    <StyledCard
      title={
        <div>
          <CardTitle>Access Tokens</CardTitle>
          <CardSubtitle>Manage API tokens for third-party integrations.</CardSubtitle>
        </div>
      }
      extra={
        <Button type="primary" icon="plus" onClick={openCreateTokenWizard}>
          Create Token
        </Button>
      }
    >
      <Table
        rowKey={record => String(record._id)}
        columns={columns}
        dataSource={tokenList}
        loading={isLoading}
        scroll={{ x: true }}
        showSorterTooltip={false}
        onChange={(pagination, filters, sorter) => {
          const sanitizedFilters = Object.entries(filters).reduce((acc, [key, values]) => {
            if (values && values.length > 0) acc[key] = values;
            return acc;
          }, {});
          handleOnAggregationTableChange({ pagination, filters: sanitizedFilters, sorter }, setQuery);
        }}
        pagination={{ total, current: query.currentPage, pageSize: query.limit }}
      />

      <Modal
        title="Create Access Token"
        visible={createWizardOpen}
        onCancel={() => {
          setCreateWizardOpen(false);
          setCreateWizardStep(0);
        }}
        footer={null}
        width={720}
        destroyOnClose
        style={{ top: 32 }}
      >
        <Steps current={createWizardStep} size="small" style={{ marginBottom: 20 }}>
          <Steps.Step title="Host mapping" description="New mapping or re-use template" />
          <Steps.Step title="Select properties" />
        </Steps>

        {createWizardStep === 0 && (
          <div style={{ height: 508, display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Token Name</div>
              <Input
                placeholder="e.g. Test Host"
                value={createWizardTokenName}
                onChange={e => setCreateWizardTokenName(e.target.value)}
                style={{ marginBottom: 16 }}
              />
              <div style={{ marginBottom: 8, fontWeight: 500 }}>API scope</div>
              <Select
                mode="multiple"
                placeholder="Select at least one"
                style={{ width: '100%', marginBottom: 16 }}
                value={createWizardApiScopes}
                onChange={setCreateWizardApiScopes}
              >
                {API_SCOPE_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
              <Tabs
                activeKey={createWizardMappingTab}
                onChange={key => {
                  setCreateWizardMappingTab(key);
                  setCreateWizardParentHostId(null);
                  setCreateWizardChildHostIds([]);
                  setCreateWizardTemplateId(null);
                  if (key === 'new') loadCreateWizardHostTree();
                  else {
                    setCreateWizardPropertyRows([]);
                    setCreateWizardSelectedIds([]);
                    loadCreateWizardTemplateOptions();
                  }
                }}
              >
                <Tabs.TabPane tab="New host mapping" key="new">
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>Host</div>
                  <Select
                    showSearch
                    placeholder="Select a host"
                    style={{ width: '100%', marginBottom: 16 }}
                    value={createWizardParentHostId || undefined}
                    onChange={value => {
                      setCreateWizardParentHostId(value || null);
                      setCreateWizardChildHostIds([]);
                    }}
                    optionFilterProp="label"
                    filterOption={(input, option) => {
                      const label = option && option.props ? option.props.label : '';
                      return String(label)
                        .toLowerCase()
                        .includes(String(input || '').toLowerCase());
                    }}
                    allowClear
                  >
                    {createWizardParentOptions.map(h => (
                      <Option key={h.id} value={h.id} label={h.name}>
                        {h.name}
                      </Option>
                    ))}
                  </Select>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>Child Host(s)</div>
                  <Select
                    mode="multiple"
                    showSearch
                    placeholder={createWizardParentHostId ? 'Select child hosts (optional)' : 'Select a host first'}
                    style={{ width: '100%' }}
                    value={createWizardChildHostIds}
                    onChange={setCreateWizardChildHostIds}
                    optionFilterProp="label"
                    disabled={!createWizardParentHostId}
                    filterOption={(input, option) => {
                      const label = option && option.props ? option.props.label : '';
                      return String(label)
                        .toLowerCase()
                        .includes(String(input || '').toLowerCase());
                    }}
                  >
                    {createWizardChildHostOptions.map(h => (
                      <Option key={h.id} value={h.id} label={h.name}>
                        {h.name}
                      </Option>
                    ))}
                  </Select>
                </Tabs.TabPane>
                <Tabs.TabPane tab="Re-Map to Existing Token" key="reuse">
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>Existing token</div>
                  <Select
                    showSearch
                    placeholder={createWizardTemplateOptions.length ? 'Select a token' : 'Loading...'}
                    style={{ width: '100%', marginBottom: 16 }}
                    value={createWizardTemplateId || undefined}
                    onChange={value => {
                      setCreateWizardTemplateId(value || null);
                    }}
                    optionFilterProp="label"
                    filterOption={(input, option) => {
                      const label = option && option.props ? option.props.label : '';
                      return String(label)
                        .toLowerCase()
                        .includes(String(input || '').toLowerCase());
                    }}
                    allowClear
                  >
                    {createWizardTemplateOptions.map(h => {
                      const tokenLabel = maskTokenForDropdown(h.token || h.id);
                      const label = `${tokenLabel} — ${h.name || 'Token'}`;
                      const value = h.id;
                      return (
                        <Option key={value} value={value} label={label}>
                          {label}
                        </Option>
                      );
                    })}
                  </Select>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                    A new access token will be created, reusing the same host mappings as the template. The source token is not changed. Select
                    properties in the next step to set the new token’s mappings.
                  </Text>
                </Tabs.TabPane>
              </Tabs>
            </div>
            <div
              style={{
                flexShrink: 0,
                paddingTop: 16,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8
              }}
            >
              <Button
                onClick={() => {
                  setCreateWizardOpen(false);
                  setCreateWizardStep(0);
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  if (!(createWizardTokenName && createWizardTokenName.trim())) {
                    return message.warning('Please enter a token name');
                  }
                  if (!createWizardApiScopes.length) {
                    return message.warning('Please select at least one API scope');
                  }
                  if (createWizardMappingTab === 'new' && !createWizardParentHostId) {
                    return message.warning('Please select a host');
                  }
                  if (createWizardMappingTab === 'reuse' && !createWizardTemplateId) {
                    return message.warning('Please select an existing token');
                  }
                  setCreateWizardStep(1);
                }}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {createWizardStep === 1 && (
          <div style={{ height: 508, display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
              {createWizardStep2Loading ? (
                <div style={{ textAlign: 'center', padding: '48px 0' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 12, color: '#888' }}>Fetching properties…</div>
                </div>
              ) : (
                <>
                  {createWizardCandidatesPartial && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Limited list"
                      description="The full PMS property list was not available. Check the candidates API for this flow (see backend spec)."
                    />
                  )}
                  <p style={{ marginBottom: 12, color: '#666' }}>
                    Properties are grouped by host. Select properties to map to the new token, then Create. Must select at least 1 property for each
                    host.
                  </p>
                  <PropertyAllowlistEditorBody
                    propertyRows={createWizardPropertyRows}
                    selectedIds={createWizardSelectedIds}
                    onSelectedIdsChange={setCreateWizardSelectedIds}
                    tableScrollY={320}
                    showSyncStatus
                    groupByHost
                    hostGroupsExtAccesses={createWizardHostGroupOrder}
                    enforceMinSelected
                    enforcePerHostMinSelected
                  />
                </>
              )}
            </div>
            <div style={{ flexShrink: 0, paddingTop: 16 }}>
              <PropertyAllowlistEditorActions
                closeLabel="Back"
                onClose={() => setCreateWizardStep(0)}
                onSaveAndSync={submitCreateTokenWizard}
                primaryLabel="Create"
                primaryLoading={createWizardSubmitting}
                primaryDisabled={createWizardStep2Loading || createWizardAllowlistPrimary.disabled}
                primaryDisabledReason={createWizardAllowlistPrimary.disabledReason}
              />
            </div>
          </div>
        )}
      </Modal>
    </StyledCard>
  );
};

export default withAppContext(AccessToken);
